import 'mutationobserver-shim';
import Vue from 'vue';
import VueRouter from 'vue-router'
import './plugins/bootstrap-vue';
import App from './App.vue';
import PipiPupuAPIClient from './PipiPupuAPIClient';
import PipiPupuAuth from './PipiPupuAuth';
import VampiVoiceClient from './VampiVoiceClient';
import 'animate.css/animate.min.css';
import 'allsettled-polyfill';
import Home from './views/Home.vue'
import ChatSocket from './ChatSocket';

Vue.use(VueRouter);
Vue.config.productionTip = false;
Vue.prototype.$baseUrl = process.env.VUE_APP_PIPIPUPUBASE || `${location.protocol}//${location.host}`;
Vue.prototype.$title = process.env.VUE_APP_PIPIPUPUTITLE||'LiveJoiner';
Vue.prototype.$voiceApi = new VampiVoiceClient(process.env.VUE_APP_VAMPIVOICEAPIURL, process.env.VUE_APP_VAMPIVOICEAPIKEY);
Vue.prototype.$auth = new PipiPupuAuth();
Vue.prototype.$api = new PipiPupuAPIClient(`${process.env.VUE_APP_PIPIPUPUBASE||''}/api`, Vue.prototype.$auth);
Vue.prototype.$socket = new ChatSocket(`${process.env.VUE_APP_PIPIPUPUSOCKETBASE}/api/socket`);
if(window?.BroadcastChannel)
	Vue.prototype.$broadcastChannel = new BroadcastChannel('LiveJoiner');

Vue.prototype.$util = {
	load(promise) {
		this.loading++;
		return promise.catch(err => {
			this.error = err&&err.error||err.toString();
		}).finally(() => {
			this.loading--;
		});
	},
	copyToClipboard(text, parentElement) {
		let el = document.createElement('textarea');
		el.value = text;
		parentElement = parentElement || document.body;
		parentElement.appendChild(el);
		el.select();
		document.execCommand('copy');
		parentElement.removeChild(el);
	},
};
Vue.prototype.$recaptcha = {
	execute(action) {
		return this.getRecaptcha().then(r => r.execute(process.env.VUE_APP_RECAPTCHA_KEY, { action }));
	},
	getRecaptcha() {
		return this.loadPromise ||= new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(process.env.VUE_APP_RECAPTCHA_KEY)}`;
			script.async = true;
			script.defer = true;

			script.onload = () => {
				window.grecaptcha.ready(() => {
					resolve(window.grecaptcha);
				})
			};

			script.onerror = reject;

			document.head.appendChild(script);
		});
	},
	async cleanup() {
		if(!this.loadPromise) return;
		const r = await this.getRecaptcha();
		r.deinitialize?.();

		// Remove the script tag
		const scripts = document.querySelectorAll('script[src*="recaptcha"]');
		scripts.forEach(script => script.parentNode?.removeChild(script));

		// Clean up global objects
		delete window.grecaptcha;

		// Remove any reCAPTCHA related elements that might be left behind
		document.querySelectorAll('[class*="grecaptcha"], [id*="grecaptcha"]')?.forEach(e => e.parentNode?.removeChild(e));
		this.loadPromise = null;
	},
};

new Vue({
	router: new VueRouter({
		mode: 'history',
		base: '/app',
		linkActiveClass: 'active-tree',
		linkExactActiveClass: 'active',
		routes: [
			{ path: '/', name: 'Home', component: Home },
			{ path: '/register', name: 'Register', component: () => import(/* webpackChunkName: "appUserFlow" */ './views/Register.vue') },
			{ path: '/recover', name: 'Recover', component: () => import(/* webpackChunkName: "appUserFlow" */ './views/Recover.vue') },
			{ path: '/confirm', name: 'Confirm', component: () => import(/* webpackChunkName: "appUserFlow" */ './views/Confirm.vue') },
			{ path: '/calls', name: 'Calls', component: () => import(/* webpackChunkName: "appCalls" */ './views/Calls.vue') },
			{ path: '/urls', name: 'URLs', component: () => import(/* webpackChunkName: "appPages" */ './views/URLs.vue') },
			{ path: '/chatters', name: 'Chatters', component: () => import(/* webpackChunkName: "appPages" */ './views/Chatters.vue') },
			{ path: '/reports', name: 'Reports', component: () => import(/* webpackChunkName: "appReports" */ './views/Reports.vue') },
			{ path: '/commands', name: 'Commands', component: () => import(/* webpackChunkName: "appPages" */ './views/Commands.vue') },
			{ path: '/timers', name: 'Timers', component: () => import(/* webpackChunkName: "appPages" */ './views/Timers.vue') },
			{
				path: '/widgets',
				component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets.vue'),
				children: [
					{ path: '', name: 'Widgets', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/All.vue') },
					{ path: 'chat', name: 'Chat widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/Chat.vue') },
					{ path: 'viewer-count', name: 'Viewer count widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/ViewerCount.vue') },
					{ path: 'highlighted-message', name: 'Highlighted message widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/HighlightedMessage.vue') },
					{ path: 'emote-wall', name: 'Emote wall widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/EmoteWall.vue') },
					{ path: 'ban-reaper', name: 'Ban reaper widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/BanReaper.vue') },
					{ path: 'gift', name: 'Alert widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/Gift.vue') },
					{ path: 'gift-goal', name: 'Gift goal widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/GiftGoal.vue') },
					{ path: 'replacements', name: 'Word Replacements', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/WordReplacements.vue') },
					{ path: 'voices', name: 'TTS Voices', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/Voices.vue') },
					{ path: 'dono-media', name: 'Donation Media', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/DonoMedia.vue') },
					{ path: 'event-media', name: 'Event Media', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/EventMedia.vue') },
					{ path: 'tower-detector', name: 'Tower Detector', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/TowerDetector.vue') },
					{ path: 'word-cloud', name: 'Word cloud widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/WordCloud.vue') },
				{ path: 'scroller', name: 'Scroller widget', component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/Scroller.vue') },
				{ path: 'latest-follower',   name: 'Latest follower widget',    component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/LatestFollower.vue') },
				{ path: 'latest-subscriber', name: 'Latest member widget',  component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/LatestSubscriber.vue') },
				{ path: 'latest-donation',   name: 'Latest donation widget',    component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/LatestDonation.vue') },
				{ path: 'top-donation',      name: 'Top donation widget',       component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/TopDonation.vue') },
				{ path: 'sub-goal',          name: 'Membership goal widget',  component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/SubGoal.vue') },
				{ path: 'follower-goal',     name: 'Follower goal widget',       component: () => import(/* webpackChunkName: "appPages" */ './views/Widgets/FollowerGoal.vue') },
				{ path: 'thresholds', name: 'Song Request Thresholds', component: () => import(/* webpackChunkName: "appPages" */ './views/Settings/Thresholds.vue') },
				],
			},
			{ path: '/songs', name: 'Songs', component: () => import(/* webpackChunkName: "appPages" */ './views/Songs.vue') },
			{ path: '/channelDemo', name: 'Channel Demo', component: () => import(/* webpackChunkName: "appChannelDemo" */ './views/ChannelDemo.vue') },
			{
				path: '/settings',
				component: () => import(/* webpackChunkName: "appPages" */ './views/Settings.vue'),
				children: [
					{ path: '', name: 'Preferences', component: () => import(/* webpackChunkName: "appPages" */ './views/Settings/Preferences.vue') },
					{ path: 'notifications', name: 'Notifications', component: () => import(/* webpackChunkName: "appPages" */ './views/Settings/Notifications.vue') },
					{ path: 'bridging', name: 'Bridging', component: () => import(/* webpackChunkName: "appPages" */ './views/Settings/Bridging.vue') },
					{ path: 'restreaming', name: 'Restreaming', component: () => import(/* webpackChunkName: "appPages" */ './views/Settings/Restreaming.vue') },
					],
			},
			{
				path: '/admin',
				component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin.vue'),
				children: [
					{ path: '', name: 'Admin', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Heap.vue') },
					{ path: 'services', name: 'Services', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Services.vue') },
					{ path: 'service/:id', name: 'Service', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Service.vue') },
					{ path: 'users', name: 'Users', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Users.vue') },
					{ path: 'voices', name: 'Voices', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Voices.vue') },
					{ path: 'channels', name: 'Channels', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Channels.vue') },
					{ path: 'channel/:userId/:serviceId/:token', name: 'Channel', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Channel.vue') },
					{ path: 'giftTypes', name: 'GiftTypes', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/GiftTypes.vue') },
					{ path: 'emotes', name: 'Emotes', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Emotes.vue') },
					{ path: 'categories', name: 'Categories', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Categories.vue') },
					{ path: 'category/:id', name: 'Category', component: () => import(/* webpackChunkName: "appAdmin" */ './views/Admin/Category.vue') },
				],
			},
			{
				path: '/account',
				component: () => import(/* webpackChunkName: "appAccount" */ './views/Account.vue' ),
				children: [
					{ path: '', name: 'Account', component: () => import(/* webpackChunkName: "appAccount" */ './views/Account/User.vue') },
					{ path: 'billing', component: () => import(/* webpackChunkName: "appAccount" */ './views/Account/Billing.vue') },
					{ path: 'email', component: () => import(/* webpackChunkName: "appAccount" */ './views/Account/Email.vue') },
					{ path: 'profile', component: () => import(/* webpackChunkName: "appAccount" */ './views/Account/Profile.vue') },
				],
			},
		],
	}),
	render: h => h(App)
}).$mount('#app');
